import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, NgModule, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterModule } from '@angular/router';
import { applyLanguageFilteringSingle } from '@app/common/filtering-behavior';
import { FeatureToggleModule } from '@app/directives/feature-toggle.directive';
import { BroadcastMessage } from '@app/models/broadcast-message';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { UserChannelTerm } from '@app/models/user-channel-term';
import { LinkModule } from '@app/directives/link.directive';
import { BroadcastService } from '@app/services/broadcast.service';
import { ChannelService } from '@app/services/channel.service';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { forkJoin, iif, Observable, of, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { BroadcastBannerListComponentModule } from '../broadcast-banner-list/broadcast-banner-list.component';

@Component({
  selector: 'app-broadcasts',
  templateUrl: './broadcasts.component.html',
  styleUrls: ['./broadcasts.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BroadcastsComponent implements OnInit, OnDestroy {

  @Input() broadcastMessages$: Observable<Array<BroadcastMessage>>;
  subscription: Subscription;
  channels$: Observable<Array<SimplifiedTerm>>;
  userChannelTerms$: Observable<Array<UserChannelTerm>>;
  @Input() showHideButton = true;

  constructor(
    private service: BroadcastService,
    private componentEventService: ComponentStatesOnRoutesService,
    public elementRef: ElementRef,
    private featureSettingsStoreService: FeatureSettingsStoreService,
    private channelService: ChannelService,
    private userChannelTermsService: UserChannelTermsService,
    private userLanguageTermService: UserLanguageTermService) {
  }

  ngOnInit() {
    this.channels$ = this.channelService.getChannels();
    this.userChannelTerms$ = this.userChannelTermsService.getUserChannelTerms();

    if (!this.broadcastMessages$) {
      const broadcastMessages$ = this.service.refresh$
        .pipe(
          mergeMap(() => {
            return forkJoin([
              this.service.getActiveBroadcastMessages(),
              this.service.getMyHiddenBroadcastMessages(),
              this.userChannelTerms$
            ]);
          }),
          map(([broadcasts, hiddenBroadcasts, userChannelTerms]) => {
            // exclude the app boradcast messages.
            const siteBroadcasts = broadcasts.filter(b => b.AppTileBroadcastMessages.length === 0);
            return siteBroadcasts.filter(b => {
              const channelTerms = b.ChannelTermBroadcastMessages;
              let match = false;
              if (channelTerms && channelTerms.length > 0) {
                match = channelTerms.some(c => userChannelTerms.some(ct => ct.ChannelTermId === c.ChannelTermId));
              } else {
                match = true;
              }
              if (match) {
                if (hiddenBroadcasts.some(h => h.Id === b.Id)) {
                  return false;
                } else {
                  return true;
                }
              } else {
                return false;
              }
            });
          }));

      this.broadcastMessages$ = broadcastMessages$
        .pipe(
          mergeMap((broadcasts) => {
            // Language filter toggle
            return iif(
              () => this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace,
              this.userLanguageTermService.getUserLanguageTerms().pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(broadcasts, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                })
              ),
              of(broadcasts)
            );
          }));
    }

    this.subscription = this.componentEventService
      .state$
      .pipe(
        filter(componentsOnRouteState => {
          return componentsOnRouteState.MainMap.some(s => s.ComponentId === this.elementRef.nativeElement.localName && s.Refresh);
        }))
      .subscribe(componentsOnRouteState => {
        if (componentsOnRouteState) {
          this.service.reload();
          const componentState = componentsOnRouteState.MainMap.find(s => s.ComponentId === this.elementRef.nativeElement.localName && s.Refresh);
          componentState.Refresh = false;
          this.componentEventService.updateStates(componentState);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

}
@NgModule({
  declarations: [BroadcastsComponent],
  exports: [BroadcastsComponent],
  imports: [
    CommonModule,
    RouterModule,
    MatExpansionModule,
    MatButtonModule,
    FeatureToggleModule,
    LinkModule,
    BroadcastBannerListComponentModule
  ]
})
export class BroadcastsModule { }
